import React from 'react';
import { ArrowRight, ChevronDown, Rocket, Briefcase, Crown, MapPin, Mail, CopyIcon, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HowItWorksSection } from './HowItWorksSection';

// Floating Candidate Card - "Product Peek" Component
const CandidatePreviewCard: React.FC = () => {
  return (
    <div className="relative w-full max-w-sm">
      {/* Main Card */}
      <div className="bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden relative z-10">
        {/* Gradient Header */}
        <div className="h-16 bg-gradient-to-r from-blue-500 to-blue-700 relative">
          {/* Rank Badge */}
          <div className="absolute -bottom-4 left-5 bg-white rounded-xl shadow-lg px-3 py-1.5 flex items-center gap-1.5 border border-slate-100">
            <Crown className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-bold text-slate-900">#1 Match</span>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pt-8 pb-5">
          {/* Name and Role */}
          <div className="mb-3">
            <h3 className="text-lg font-bold text-slate-900">Alex Davis</h3>
            <p className="text-sm text-slate-600 font-medium">Lead Software Engineer</p>
          </div>

          {/* Quick Info Row */}
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span>London, UK</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              <span>10 Years</span>
            </div>
          </div>

          {/* Email Unlocked Component (Mirroring Reality) */}
          <div className="mb-4 flex gap-2">
            <div className="flex-1 bg-gray-50/30 p-3 border border-gray-100 rounded-xl flex items-center justify-between gap-3 shadow-sm overflow-hidden min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-1.5 bg-white text-gray-600 rounded-lg shadow-sm border border-gray-50">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="font-mono text-slate-700 font-medium text-[11px] truncate">alex.kdavis@gmail.com</p>
                </div>
              </div>
              <button className="flex items-center justify-center p-1.5 bg-white text-slate-400 hover:text-slate-600 rounded-lg border border-slate-100 transition-colors flex-shrink-0">
                <CopyIcon className="w-3 h-3" />
              </button>
            </div>

            <button className="px-4 bg-gray-50 text-gray-600 rounded-xl shadow-sm hover:bg-gray-100 transition-all flex items-center gap-2 group flex-shrink-0 outline outline-1 outline-gray-100">
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              <span className="text-[10px] font-bold uppercase tracking-wider">CV</span>
            </button>
          </div>

          {/* Short Summary */}
          <p className="text-xs text-slate-600 leading-relaxed mb-4 italic line-clamp-2">
            "Expertise in architecting scalable distributed systems and leading cross-functional engineering teams at Series B startups..."
          </p>

          {/* Highlight Tags - Key Matches to JD */}
          <div className="mb-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Top Attributes</p>
            <div className="flex flex-wrap gap-1.5">
              <div className="flex items-center gap-1 px-2 py-1 bg-orange-50 rounded-lg border border-orange-100">
                <Rocket className="w-3 h-3 text-orange-600" />
                <span className="text-[10px] font-semibold text-orange-800">Startup Experience</span>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="mb-5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Skills</p>
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-medium">Node.js</span>
              <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-medium">AWS</span>
              <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-medium">PostgreSQL</span>
              <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-medium">React.js</span>
            </div>
          </div>

          {/* Unlock CTA */}
          <button className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm">
            <span>View Resume</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Background Cards (Stacked) */}
      <div className="absolute top-4 -right-3 -z-10 w-full h-full bg-white/80 rounded-2xl border border-slate-100 shadow-lg transform rotate-3" />
      <div className="absolute top-8 -right-6 -z-20 w-full h-full bg-white/60 rounded-2xl border border-slate-100 shadow-md transform rotate-6" />
    </div>
  );
};

export const RecruiterHero: React.FC = () => {
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="w-full flex-1 flex flex-col">
      {/* Hero Section Container */}
      <div className="flex-1 min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-4 md:px-6">
        <div className="max-w-6xl mx-auto w-full">
          {/* Split Layout: Text Left, Card Right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left Side - Text Content */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 shadow-sm mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                </span>
                <span className="text-sm font-medium text-slate-600">
                  Post. Get Matched. Hire.
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-[1.08] tracking-tight mb-5">
                Discover the best talent{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    10x Faster.
                  </span>
                  {/* Static decorative underline */}
                  <div className="absolute -bottom-1 left-0 w-full h-2 bg-gradient-to-r from-blue-200/40 via-blue-300/60 to-blue-200/40 rounded-full -z-10" />
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-slate-600 max-w-xl mb-8 leading-relaxed">
                JobVital matches your job postings with only the top and most relevant
                talent — no keyword guessing, no resume spam.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
                <button
                  onClick={() => navigate('/hiring-signup')}
                  type="button"
                  className="group relative inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-full font-semibold text-base transition-all duration-200 hover:bg-slate-800 hover:shadow-lg hover:shadow-slate-900/20 w-full sm:w-auto"
                >
                  <span>Start Hiring</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>

                <button
                  onClick={scrollToHowItWorks}
                  type="button"
                  className="group inline-flex items-center justify-center gap-2 text-slate-600 font-medium text-base transition-all duration-200 hover:text-slate-900 w-full sm:w-auto px-4 py-4"
                >
                  <span>See How It Works</span>
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                </button>
              </div>
            </div>

            {/* Right Side - Product Peek Card */}
            <div className="flex justify-center lg:justify-end">
              <CandidatePreviewCard />
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Pricing Preview */}
      {/* <PricingPreview /> */}
    </div>
  );
};
